import { formatData } from '@utilities';

/**
 * @typedef ExportQuery
 * @property {string} [compare_by]
 * @property {string} [language]
 * @property {string} [option_filter]
 * @property {string} [quota_group_filter]
 */

/**
 * @typedef CompletionRateData
 * @property {number} completes
 * @property {number} disqualified
 * @property {string} disqualification_rate_pct
 * @property {string} segment
 */

/**
 * @typedef {Object} CompletionRatePayload
 * @property {Object[]} data
 * @property {Object} data.table
 * @property {import('@utilities/components/TableData').TableDataColumn[]} data.table.columns
 * @property {CompletionRateData[]} data.table.data
 */

/**
 * convertChartToColumnRange
 * @param {Object} chart
 * @param {Object[]} chart.series
 * @return {Object}
 */
const convertChartToColumnRange = ({ series, ...chart }) => {
  const seriesLeftStart = chart.xAxis.categories.map((_, index) => {
    return series
      .slice(0, Math.floor(series.length / 2))
      .reduce((total, { data }) => total + data[index], 0);
  });

  return {
    ...chart,
    series: series.map(({ data, id, name }, index) => {
      return {
        data: data.map((value, index) => {
          const low = seriesLeftStart[index];
          seriesLeftStart[index] += Math.abs(value);

          return {
            high: low + Math.abs(value),
            low: low,
          };
        }),
        id,
        name: index === 0 ? `${name} (LOW)` : index === series.length - 1 ? `${name} (HIGH)` : name,
      };
    }),
  };
};

/**
 * transformColumn - capitalizes Header for FE table purposes
 * @param {Object} column
 * @param {string} column.accessor
 * @param {number|string?} [column.footer]
 * @param {string?} [column.footer_format]
 * @param {string?} [column.format]
 * @param {string} column.header
 * @param {string?} [column.row_span_by]
 * @param {string?} [column.variant]
 * @return {Object}
 */
export const transformColumn = ({
  accessor,
  footer,
  footer_format: footerFormat,
  format,
  header,
  row_span_by: rowSpanBy,
  variant,
}) => ({
  accessor,
  ...(format && {
    Cell: ({ value }) => formatData({ format, value }),
  }),
  ...(footer && {
    Footer: footerFormat ? formatData({ format: footerFormat, value: footer }) : footer,
  }),
  format,
  Header: header,
  rowSpanBy,
  ...(variant && { variant }),
});

/**
 * transformContinuousSumResponses
 * @param {Object} response
 * @param {Object[]} response.data
 * @return {Object[]}
 */
export const transformContinuousSumResponses = ({ data }) => {
  return data.map(({ id, table_count, table_pct, type }) => ({
    id: Number(id),
    tableCount: {
      columns: table_count.columns.map(transformColumn),
      data: table_count.data,
    },
    tablePct: {
      columns: table_pct.columns.map(transformColumn),
      data: table_pct.data,
    },
    type,
  }));
};

/**
 * transformDetails
 * @param {Object} response
 * @param {number} response.completes
 * @param {Object} response.completes_by_quota_group
 * @param {number} response.id
 * @param {number} response.rawdata_row_limit
 * @return {Object}
 */
export const transformDetails = ({
  completes,
  completes_by_quota_group: completesByQuotaGroup,
  id,
  rawdata_row_limit: rawDataRowLimit,
}) => ({
  completes,
  completesByQuotaGroup,
  id,
  rawDataRowLimit,
});

/**
 * transformEssayResponses
 * @param {Object} response
 * @param {Object[]} response.data
 * @return {Object[]}
 */
export const transformEssayResponses = ({ data }) =>
  data.map(({ id, segments, ...question }) => ({
    id: Number(id),
    ...question,
    segments: segments.map(({ table, ...segment }) => ({
      ...segment,
      table: {
        columns: table.columns.map(transformColumn),
        data: table.data,
      },
    })),
  }));

/**
 * transformGlobalFiltersV2
 * @param {Object} filters
 * @param {Object[]} filters.demographic_filters
 * @param {Object[]} filters.quota_groups
 * @return {Object}
 */
export const transformGlobalFiltersV2 = ({ demographic_filters, quota_groups }) => ({
  demographicFilters: demographic_filters.map(({ compare_by: compareBy, id, options, title }) => ({
    compareBy,
    id,
    label: title,
    options,
  })),
  quotaGroups: quota_groups.map(({ name, quota_group_refs: quotaGroupRefs }) => {
    const normalizedQuotaGroupRefs = quotaGroupRefs.map(String);

    return {
      instantSurveyGroupRef: normalizedQuotaGroupRefs[0],
      name,
      quotaGroupRefs: normalizedQuotaGroupRefs,
    };
  }),
});

/**
 * transformGlobalFilters
 * @param {Object} filters
 * @param {Object[]} filters.demographic_filters
 * @param {Object[]} filters.quota_groups
 * @return {Object}
 */
export const transformGlobalFilters = ({ demographic_filters, quota_groups }) => ({
  demographicFilters: demographic_filters.map(({ compare_by: compareBy, id, options, title }) => ({
    compareBy,
    id,
    label: title,
    options,
  })),
  quotaGroups: quota_groups.map(
    ({ instant_survey_group_ref, name, quota_group_refs: quotaGroupRefs }) => ({
      instantSurveyGroupRef: String(instant_survey_group_ref),
      name,
      quotaGroupRefs,
    })
  ),
});

/**
 * transformGridSlice - transforms a single grid data slice (segments + tables).
 * Used both for a normal grid item and for each per-compare-by-group slice of a
 * sequential-monadic grid item.
 * @param {Object} slice
 * @param {Object[]} slice.segments
 * @param {Object} slice.table_count
 * @param {Object} slice.table_pct
 * @return {Object} { segments, tableCount, tablePct }
 */
const transformGridSlice = ({ segments, table_count, table_pct }) => ({
  segments: segments.map(({ chart_count, chart_pct, id, name }) => {
    return {
      chartCount: {
        ...chart_count,
        series: chart_count.series.map((series) => ({
          ...series,
          dataLabels: {
            formatter: function () {
              return formatData({ format: chart_count.format, value: this.y });
            },
          },
        })),
      },
      chartPct: {
        ...chart_pct,
        series: chart_pct.series.map((series) => ({
          ...series,
          dataLabels: {
            formatter: function () {
              return formatData({ format: chart_pct.format, value: this.y });
            },
          },
        })),
      },
      id,
      name,
    };
  }),
  tableCount: {
    columns: table_count.columns.map(transformColumn),
    data: table_count.data,
  },
  tablePct: {
    columns: table_pct.columns.map(transformColumn),
    data: table_pct.data,
  },
});

// keys that live alongside the grid data slice(s) on a response item
const GRID_ITEM_META_KEYS = ['id', 'shortname', 'type'];

/**
 * transformGridResponses
 *
 * A normal grid item carries `segments`/`table_count`/`table_pct` at the top level. A
 * sequential-monadic grid item (compare-by, e.g. quota_group) instead nests those under
 * per-group NAME keys (e.g. `"Target 1"`, `"Target 2"`). For the monadic shape we build a
 * `dataByCompareBy` map keyed by group name and default the top-level slice to the first
 * group so legacy consumers (PPTX export) keep working.
 * @param {Object} response
 * @param {Object[]} response.data
 * @return {Object[]}
 */
export const transformGridResponses = ({ data }) =>
  data.map(({ id, segments, table_count, table_pct, type, ...rest }) => {
    // normal / non-monadic grid: slice is at the top level
    if (Array.isArray(segments)) {
      return {
        id: Number(id),
        type,
        ...transformGridSlice({ segments, table_count, table_pct }),
      };
    }

    // sequential-monadic grid: slices are nested under per-compare-by-group name keys
    const groupNames = Object.keys(rest).filter(
      (key) => !GRID_ITEM_META_KEYS.includes(key) && Array.isArray(rest[key]?.segments)
    );
    const dataByCompareBy = groupNames.reduce((acc, groupName) => {
      acc[groupName] = transformGridSlice(rest[groupName]);
      return acc;
    }, {});
    const firstGroupSlice = groupNames.length ? dataByCompareBy[groupNames[0]] : {};

    return {
      dataByCompareBy,
      id: Number(id),
      type,
      ...firstGroupSlice,
    };
  });

/**
 * transformMaxDiffResponses
 * @param {Object} response
 * @param {Object[]} response.data
 * @return {Object[]}
 */
export const transformMaxDiffResponses = ({ data }) => {
  return data.map(({ id, segments, ...question }) => {
    return {
      ...question,
      id: Number(id),
      segments: segments.map(({ chart_count, chart_pct, id, name, table_count, table_pct }) => {
        return {
          chartCount: chart_count,
          chartPct: chart_pct,
          id,
          name,
          tableCount: {
            columns: table_count.columns.map(transformColumn),
            data: table_count.data,
          },
          tablePct: {
            columns: table_pct.columns.map(transformColumn),
            data: table_pct.data,
          },
        };
      }),
    };
  });
};

/**
 * transformRankResponses
 * @param {Object} response
 * @param {Object[]} response.data
 * @return {Object[]}
 */
export const transformRankResponses = ({ data }) =>
  data.map(({ id, segments, ...question }) => {
    return {
      ...question,
      id: Number(id),
      segments: segments.map(({ chart_count, chart_pct, id, name, table_count, table_pct }) => {
        return {
          chartCount: convertChartToColumnRange(chart_count),
          chartPct: convertChartToColumnRange(chart_pct),
          id,
          name,
          tableCount: {
            columns: table_count.columns.map(transformColumn),
            data: table_count.data,
          },
          tablePct: {
            columns: table_pct.columns.map(transformColumn),
            data: table_pct.data,
          },
        };
      }),
    };
  });

/**
 * transformRawData
 * @param {Object} response
 * @param {Object[]} response.data
 * @return {Object[]}
 */
export const transformRawData = ({ data }) =>
  data.map(({ table }) => ({
    table: {
      columns: table.columns.map(transformColumn),
      data: table.data,
    },
  }));

const getTurfSimulatorDataColumns = (items = []) => {
  const accessors = items.reduce((columns, row) => {
    if (!row || typeof row !== 'object') return columns;

    Object.keys(row).forEach((accessor) => columns.add(accessor));

    return columns;
  }, new Set());

  return Array.from(accessors).map((accessor) =>
    transformColumn({
      accessor,
      header: accessor,
    })
  );
};

/**
 * transformTurfSimulatorData
 * @param {Object[]} items
 * @return {Object[]}
 */
export const transformTurfSimulatorData = (items = []) => [
  {
    table: {
      columns: getTurfSimulatorDataColumns(items),
      data: items,
    },
  },
];

/**
 * Adds a Highcharts dataLabels formatter to each series of a bar chart.
 *
 * Handles both chart shapes returned by the survey-responses endpoint:
 *  - Standard: `{ format, series: [...] }` — the answer options are the x-axis and each
 *    compare-by group is a series.
 *  - Sequential monadic: `{ format, "<answer option>": { series: [...] }, ... }` — the concepts
 *    are the x-axis instead, and every answer option carries its own set of per-group series, so
 *    there is no top-level `series`.
 *
 * The keyed shape is preserved so `normalizeAnswerOptionChart` can pick the series for the
 * selected answer option at render time.
 */
export const formatChartSeries = (chart) => {
  if (!chart) return chart;

  const withFormatter = (series) =>
    series.map((s) => ({
      ...s,
      dataLabels: {
        formatter: function () {
          return formatData({ format: chart.format, value: this.y });
        },
      },
    }));

  if (Array.isArray(chart.series)) {
    return { ...chart, series: withFormatter(chart.series) };
  }

  return Object.entries(chart).reduce((acc, [key, value]) => {
    acc[key] =
      value && Array.isArray(value.series)
        ? { ...value, series: withFormatter(value.series) }
        : value;
    return acc;
  }, {});
};

/**
 * transformSurveyResponses
 * @param {Object} response
 * @param {Object[]} response.data
 * @return {Object[]}
 */
export const transformSurveyResponses = ({ data }) =>
  data.map(
    ({ chart_count, chart_pct, id, methodologies, table_count, table_other, table_pct, type }) => {
      return {
        chartCount: formatChartSeries(chart_count),
        chartPct: formatChartSeries(chart_pct),
        id: Number(id),
        tableCount: {
          columns: table_count.columns.map(transformColumn),
          data: table_count.data,
        },
        tableOther: {
          columns: table_other?.columns.map(transformColumn),
          data: table_other?.data.map(({ data, id, name: text }) => ({
            id,
            subRows: data.sort(
              ({ text: textA }, { text: textB }) =>
                textB.length - textA.length || textA.localeCompare(textB)
            ),
            text,
          })),
        },
        tablePct: {
          columns: table_pct.columns.map(transformColumn),
          data: table_pct.data,
        },
        title: chart_pct?.title || chart_count?.title,
        turfAnalysis: methodologies?.turf
          ? {
              segments: methodologies?.turf?.segments?.map(({ chart, id, name, table }) => ({
                chart: {
                  ...chart,
                  series: chart.series.map((series) => ({
                    ...series,
                    dataLabels: {
                      formatter: function () {
                        return formatData({ format: chart.format, value: this.y });
                      },
                    },
                  })),
                },
                id,
                name,
                table: {
                  columns: table.columns.map(transformColumn),
                  data: table.data,
                },
              })),
            }
          : null,
        type,
      };
    }
  );

/**
 * transformVideoResponses
 * @param {Object} response
 * @param {Object[]} response.data - contains per-question video items and survey-level highlight items (resource_type video_summary / video_highlights)
 * @return {Object[]}
 */
export const transformVideoResponses = ({ data }) => {
  const videoQuestions = data
    .filter(({ responses }) => Array.isArray(responses))
    .map(({ id, responses, type }) => ({
      id: Number(id),
      segments: responses.map(({ compare_by, quota_group_ref, responses: segmentResponses }) => ({
        compareBy: compare_by,
        name: compare_by,
        quotaGroupRef: quota_group_ref,
        responses: segmentResponses.map(
          ({
            ai_summary,
            audio_segments,
            items,
            response_id,
            thumbnail_url,
            user_ref,
            video_url,
          }) => ({
            aiSummary: ai_summary,
            audioSegments: audio_segments,
            items,
            responseId: response_id,
            thumbnailUrl: thumbnail_url,
            userRef: user_ref,
            videoUrl: video_url,
          })
        ),
      })),
      type,
    }));

  const videoSummaryItem = data.find(({ resource_type }) => resource_type === 'video_summary');
  const videoHighlightsItem = data.find(
    ({ resource_type }) => resource_type === 'video_highlights'
  );
  const highlightsUrl = videoHighlightsItem?.url ?? null;
  const summaryText = videoSummaryItem?.text ?? null;
  if (highlightsUrl || summaryText) {
    videoQuestions.push({
      expiresAt: videoHighlightsItem?.expires_at ?? null,
      highlightsUrl,
      id: 'VIDEO_HIGHLIGHTS',
      summaryText,
      type: 'VIDEO_HIGHLIGHTS',
    });
  }

  return videoQuestions;
};

/**
 *
 * @param {Object} options
 * @param {string} [options.publicToken]
 * @returns {boolean} true if publicToken is not defined
 */
export const getWithCredentials = ({ publicToken }) => (publicToken ? false : true);
